import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

const overlay = 'fixed inset-0 z-50 bg-black/45 backdrop-blur-[2px]';
/**
 * ⚠️ `max-h` and `flex-col` are load-bearing, not polish.
 *
 * The panel is centred with `top-1/2 -translate-y-1/2`. Without a height cap a
 * tall form grows past the viewport in BOTH directions, and the overflowing top
 * is unreachable — a `fixed` element does not scroll with the page, so the
 * first fields and any error on them simply cannot be seen or corrected. It
 * showed up first on the calendar event form, which has the most fields.
 *
 * Capping the panel and letting `DialogBody` scroll keeps the title and the
 * footer buttons on screen, which is the behaviour that matters: a form whose
 * Save button has scrolled off is a form nobody can submit.
 */
const panel =
  'fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col rounded-card border border-line bg-surface shadow-2xl';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export function DialogContent({
  className,
  children,
  title,
  description,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  title: string;
  description?: string;
}): React.JSX.Element {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={overlay} />
      <DialogPrimitive.Content className={cn(panel, className)} {...props}>
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div>
            <DialogPrimitive.Title className="text-sm font-semibold text-ink">
              {title}
            </DialogPrimitive.Title>
            {description ? (
              <DialogPrimitive.Description className="mt-1 text-xs text-ink-soft">
                {description}
              </DialogPrimitive.Description>
            ) : null}
          </div>
          <DialogPrimitive.Close
            aria-label="Close"
            className="rounded p-1 text-ink-faint transition-colors hover:bg-surface-sunken hover:text-ink"
          >
            <X className="size-4" aria-hidden="true" />
          </DialogPrimitive.Close>
        </div>
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

/**
 * A `<form>` that keeps the dialog's scroll working.
 *
 * ⚠️ **A form between `DialogContent` and `DialogBody` breaks the flex chain,
 * and this is the trap this component exists to close.** `DialogContent` caps
 * its height and lays out a column; `DialogBody` claims the leftover space with
 * `flex-1` and scrolls. Put a plain `<form>` in between and the body's `flex-1`
 * has nothing to measure against — the form grows to its content height and
 * bursts straight through the cap, so the panel runs off the screen with no way
 * to reach the fields at the bottom.
 *
 * Nine dialogs had it. Fixing them individually would have left the tenth to
 * rediscover it, so the correct classes live here instead.
 */
export function DialogForm({
  className,
  ...props
}: React.FormHTMLAttributes<HTMLFormElement>): React.JSX.Element {
  return <form className={cn('flex min-h-0 flex-1 flex-col', className)} {...props} />;
}

export function DialogBody({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  // `min-h-0` is required for a flex child to be allowed to shrink below its
  // content height; without it `overflow-y-auto` never engages and the panel
  // grows instead of scrolling.
  return (
    <div
      className={cn('min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4', className)}
      {...props}
    />
  );
}

export function DialogFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  return (
    <div
      className={cn(
        // shrink-0 so the Save button never scrolls away with the content.
        'flex shrink-0 items-center justify-end gap-2 border-t border-line bg-surface-sunken/60 px-5 py-3',
        className,
      )}
      {...props}
    />
  );
}

// ---------------------------------------------------------------------------
// Row actions
// ---------------------------------------------------------------------------

export const DropdownMenu = DropdownPrimitive.Root;
export const DropdownMenuTrigger = DropdownPrimitive.Trigger;

export function DropdownMenuContent({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownPrimitive.Content>): React.JSX.Element {
  return (
    <DropdownPrimitive.Portal>
      <DropdownPrimitive.Content
        sideOffset={4}
        align="end"
        className={cn(
          'z-50 min-w-48 overflow-hidden rounded-md border border-line bg-surface p-1 shadow-xl',
          className,
        )}
        {...props}
      />
    </DropdownPrimitive.Portal>
  );
}

export function DropdownMenuItem({
  className,
  destructive,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownPrimitive.Item> & {
  destructive?: boolean;
}): React.JSX.Element {
  return (
    <DropdownPrimitive.Item
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm outline-none transition-colors',
        'data-[highlighted]:bg-surface-sunken data-[disabled]:pointer-events-none data-[disabled]:opacity-40',
        destructive ? 'text-danger data-[highlighted]:bg-danger-wash' : 'text-ink',
        className,
      )}
      {...props}
    />
  );
}

export function DropdownMenuSeparator(): React.JSX.Element {
  return <DropdownPrimitive.Separator className="my-1 h-px bg-line" />;
}

export function DropdownMenuLabel({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownPrimitive.Label>): React.JSX.Element {
  return (
    <DropdownPrimitive.Label
      className={cn(
        'px-2 py-1.5 text-2xs font-semibold tracking-wide text-ink-faint uppercase',
        className,
      )}
      {...props}
    />
  );
}
